import { Box, Flex, Text } from '@chakra-ui/react';
import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Modal, useToast } from '@morressier/alembic';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useUpdatedUserInfoMutation } from 'src/api/signUpMutation';
import {
  handleDuplicateCheck,
  IOrganizationForm,
  pickFields,
  stepTwoFormFields,
  useUserDetailsData
} from 'src/components/UserRegistrationSteps/StepThree/signupHook';
import MultipleAffiliationForm from 'src/components/UserRegistrationSteps/StepTwo/MultipleAffiliationForm';

const EditAdditionalAffiliationDetailsModal = ({
  data,
  organizationId
}: {
  data: {
    additionalAffiliations: IOrganizationForm[];
    affiliationDetails: IOrganizationForm;
  };
  organizationId: string;
}) => {
  const updateAffiliationMutation = useUpdatedUserInfoMutation();
  const toast = useToast();
  const { configs } = useUserDetailsData(organizationId || '');
  const newConfig = {
    streetAddress: configs.StreetAddress,
    city: configs.City,
    stateOrRegion: configs.StateOrRegion,
    postalCode: configs.PostalCode,
    country: configs.Country,
    url: configs.Url,
    department: configs.Department,
    organizationName: configs.OrganizationName
  };
  const [isOpen, setIsOpen] = useState(false);

  const methods = useForm<{
    additionalAffiliations: IOrganizationForm[];
    affiliationDetails: IOrganizationForm;
  }>({ mode: 'onChange' });
  const { handleSubmit, getValues, setValue } = methods;

  const onClose = () => {
    setIsOpen(false);
  };
  const constructPayload = () => {
    const { additionalAffiliations } = getValues();
    return {
      section: 'ADDITIONAL_AFFILIATIONS',
      additionalAffiliations
    };
  };
  const handleEditAffiliationSubmit = async () => {
    try {
      const { affiliationDetails, additionalAffiliations } = getValues();

      const filteredAffiliationDetails = pickFields(
        affiliationDetails || {},
        stepTwoFormFields
      );
      const filteredAdditionalAffiliations =
        additionalAffiliations?.map(a => pickFields(a, stepTwoFormFields)) ||
        [];

      const lastIdx = filteredAdditionalAffiliations.length - 1;
      const selectedAffiliation = filteredAdditionalAffiliations[lastIdx];

      if (selectedAffiliation && filteredAdditionalAffiliations.length > 0) {
        const isDuplicateMatches = handleDuplicateCheck(
          selectedAffiliation,
          `additionalAffiliations.${lastIdx}`,
          filteredAdditionalAffiliations,
          filteredAffiliationDetails
        );

        if (isDuplicateMatches.length) {
          const duplicatesLabel = isDuplicateMatches
            .map(m =>
              m.index === 'primary'
                ? 'Primary affiliation'
                : `Secondary affiliation(s) ${Number(m.index) + 1}`
            )
            .join(', ');

          toast({
            variant: 'error',
            title: 'Duplicate affiliation detected',
            description: `Secondary affiliation(s) ${
              lastIdx + 1
            } is identical to ${duplicatesLabel}. Please modify one of these entries to continue.`,
            isClosable: true
          });

          return; // stop if any duplicate field
        }
      }
      const response = await updateAffiliationMutation.mutateAsync({
        payload: constructPayload()
      });

      if (!response.isSuccess) {
        const message =
          response?.message?.details?.[0]?.message ||
          response?.message?.message ||
          'Internal Server Error';
        toast({
          variant: 'error',
          title: 'Affiliation Error',
          description: message,
          isClosable: true
        });
        return;
      }

      toast({
        variant: 'success',
        title: 'Success',
        description: response?.message ?? 'Updated successfully',
        isClosable: true
      });
      onClose();
    } catch (err) {
      toast({
        variant: 'error',
        title: 'Error',
        description: 'Internal Server Error',
        isClosable: true
      });
    }
  };

  useEffect(() => {
    data.affiliationDetails &&
      setValue('affiliationDetails', data.affiliationDetails);
  }, []);

  return (
    <>
      <Button variant="secondary" onClick={() => setIsOpen(true)}>
        <Box>
          <FontAwesomeIcon icon={faEdit} />{' '}
        </Box>
        <Text> Edit Secondary Affiliation(s)</Text>

        <Box>
          {data?.additionalAffiliations?.length
            ? `(${data?.additionalAffiliations?.length})`
            : null}
        </Box>
      </Button>

      <Modal
        isOpen={isOpen}
        width="700px"
        onClose={() => {}}
        closeButtonProps={{
          visibility: 'hidden'
        }}
      >
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(handleEditAffiliationSubmit)}>
            <Flex direction="column" px={8} pt={8} gap={3}>
              <Box>
                <FontAwesomeIcon icon={faEdit} size="2xl" />
              </Box>
              <Text textStyle="headingGroup">
                Edit Secondary Affiliation(s)
              </Text>
              <MultipleAffiliationForm
                formConfig={newConfig}
                additionalAffiliations={data?.additionalAffiliations}
              />

              <Flex justify="flex-end" gap={3} my={5}>
                <Button variant="secondary" onClick={onClose}>
                  Exit Without Saving
                </Button>
                <Button
                  type="submit"
                  disabled={updateAffiliationMutation.isLoading}
                >
                  {updateAffiliationMutation.isLoading
                    ? 'Saving...'
                    : 'Save Changes'}
                </Button>
              </Flex>
            </Flex>
          </form>
        </FormProvider>
      </Modal>
    </>
  );
};

export default EditAdditionalAffiliationDetailsModal;
